import { Browser } from "@capacitor/browser";
import { API_TYPES } from "@clipboard-health/contract-backend-main";
import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { useCreateTen99PolicyApplicationSession } from "@src/appV2/redesign/Documents/api/useCreateTen99PolicyApplicationSession";
import { useGetTen99PolicyJobs } from "@src/appV2/redesign/Documents/api/useGetTen99PolicyJobs";
import { DocumentDetailsSection } from "@src/appV2/redesign/Documents/Details/DetailsSection";
import { useGetShiftModal } from "@src/appV2/redesign/Shift/Open/useGetShiftModal";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { PageWrapper } from "../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../components/StickyScrollPageHeader";
import { DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_FULL_PATH } from "../../../paths";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";

export function DocumentDetailsTen99PolicyCoveragePage({
  shiftId,
}: {
  shiftId: string | undefined;
}) {
  const { requirement: workerRequirement } = useRequirementDetailsContext();

  const scrollRef = useRef<HTMLDivElement>(null);
  const { status, requirementId } = workerRequirement;

  const { showErrorToast } = useToast();
  const { mutateAsync: createTen99PolicyApplicationSession, isLoading } =
    useCreateTen99PolicyApplicationSession();
  const { userId: workerId } = useDefinedWorker();
  const { data: submittedApplications, isLoading: isLoadingSubmittedApplications } =
    useGetTen99PolicyJobs(workerId, { applicationSubmitted: true });
  const {
    data: { shift, offer },
  } = useGetShiftModal(shiftId ?? "", { enabled: isDefined(shiftId) });
  const workplaceId = shift?.workplace.id;
  const payInMinorUnits = offer?.attributes.pay.amountInMinorUnits;
  const history = useHistory();
  const browserListenerRef = useRef<undefined | { remove: () => void }>();
  const hasSubmittedApplications =
    isDefined(submittedApplications) && submittedApplications.data.length > 0;
  const queryClient = useQueryClient();

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_TEN99_POLICY_DOCUMENT_PAGE, {
    requirementId,
    shiftId,
    workplaceId,
    workerId,
  });

  const canCreateApplicationSession =
    isDefined(shiftId) &&
    isDefined(workplaceId) &&
    isDefined(workerId) &&
    isDefined(payInMinorUnits) &&
    status !== WorkerRequirementStatus.APPROVED &&
    !hasSubmittedApplications &&
    !isLoadingSubmittedApplications;

  async function onSubmit() {
    try {
      if (canCreateApplicationSession) {
        const applicationSessionResponse = await createTen99PolicyApplicationSession({
          data: {
            type: API_TYPES.ten99PolicyApplicationSession,
            attributes: { wageInMinorUnits: payInMinorUnits },
            relationships: {
              shift: { data: { type: "shift", id: shiftId } },
              worker: { data: { type: "worker", id: workerId } },
              workplace: { data: { type: "workplace", id: workplaceId } },
            },
          },
        });
        const {
          data: {
            attributes: { url: applicationSessionUrl },
          },
        } = applicationSessionResponse;

        logEvent(APP_V2_USER_EVENTS.TEN99_POLICY_APPLICATION_SESSION_CREATED, {
          requirementId,
          shiftId,
          workplaceId,
          workerId,
        });

        browserListenerRef.current = await Browser.addListener("browserFinished", () => {
          logEvent(APP_V2_USER_EVENTS.TEN99_POLICY_APPLICATION_SUBMITTED_OR_ABANDONED, {
            requirementId,
            shiftId,
            workplaceId,
            workerId,
          });
          void queryClient.invalidateQueries({
            queryKey: [
              `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/1099-policy/jobs`,
            ],
          });
          history.replace(
            generatePath(DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_FULL_PATH, {
              requirementId,
            })
          );
          browserListenerRef.current?.remove();
        });

        await Browser.open({ url: applicationSessionUrl });
      }
    } catch {
      showErrorToast("Failed to complete 1099 policy application, please contact support.");
    }
  }

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={
          status === WorkerRequirementStatus.APPROVED
            ? "Workers' Compensation Coverage - 1099 Policy"
            : "Opt into Workers' Compensation coverage"
        }
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
        {hasSubmittedApplications && status !== WorkerRequirementStatus.APPROVED && (
          <DocumentDetailsSection
            title="Coverage Review"
            description="Your application has been received and is under review. Please check back soon."
          />
        )}
      </DocumentPageScrollWrapper>

      {canCreateApplicationSession && (
        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isLoading}
            onClick={async () => {
              await onSubmit();
            }}
          >
            Complete
          </LoadingButton>
        </PageFooterWrapper>
      )}
    </PageWrapper>
  );
}
